import {
  configureStyle,
  registerCssSerializer,
  scheduleStyleIntent,
  VIEW_CLASS,
  lazyStyleIntent,
  lazyGlobalStyleIntent,
  type Style,
} from '@sail/engine';
import {plugins} from './plugins';
import type {Properties} from './properties';
import {properties} from './properties';

declare module '@sail/engine' {
  interface ViewCssSerializerRegistry {
    nonUniversal: typeof css;
  }
}

type Css = Style.LazyStylesFunction & {
  global: Style.LazyGlobalStylesFunction;
  properties: Style.Properties;
  plugins: Style.Plugins;
};

function createCss(): Css {
  // Create the serializer
  const serializer = registerCssSerializer(
    'default',
    configureStyle(properties, plugins),
  );

  // The returned value of `lazyStyleIntent` is not an actual Style.Intent.
  // Styles are not expanded into atomic styles during this stage. To mantain
  // compatibility with the previous system while we roll this out, we need to
  // cast to unknown.
  const css: Css = (...args) => lazyStyleIntent(...args);
  css.global = lazyGlobalStyleIntent;
  css.properties = serializer.properties;
  css.plugins = serializer.plugins;

  // Schedule any styles necessary for normalizing views
  scheduleStyleIntent(
    css.global({
      // https://jira.corp.stripe.com/browse/SAIL-3433
      [`@supports (-webkit-hyphens:none)`]: {
        [`select.${VIEW_CLASS}, select.sail-next-select-fix`]: {
          fontFamily: `'Helvetica Neue', sans-serif !important`,
        },
      },

      [`.${VIEW_CLASS}, .${VIEW_CLASS}::before, .${VIEW_CLASS}::after`]: {
        boxSizing: 'border-box',
        // WebkitFontSmoothing: 'antialiased',
      },

      [`span.${VIEW_CLASS}`]: {
        display: 'inline',
      },

      [`fieldset.${VIEW_CLASS}`]: {
        border: 0,
        margin: 0,
        minWidth: 0,
        padding: 0,
      },

      [`legend.${VIEW_CLASS}`]: {
        padding: 0,
      },

      [`.${VIEW_CLASS} > option`]: {
        padding: 0,
      },
    }),
  );

  return css;
}

/**
 Creates a set of styles that can be applied to one or more views.
 *
 @see https://sail.stripe.me/apis/css
 */
export const css = createCss();

export type CssProperties = Parameters<typeof css>[0];

// eslint-disable-next-line @typescript-eslint/no-empty-function
function preserveReference(_value: unknown) {}

/**
 Ensure that APIs that expose the `css` prop reference the default `css`
 serializer method. This ensures that the serializer won't be omitted when
 a bundler eliminates dead code.
 */
export function usesCssSerializer<T>(value: T): T {
  preserveReference(css);
  return value;
}

// Add types to the serializer registry to support the `css` prop on views
declare module '@sail/engine' {
  interface ViewCssSerializerRegistry {
    default: typeof css;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface CssProperties extends Properties {}
}

export const external = <T extends Style.Intent | Style.Intent[]>(
  value: T,
): T => value;

// Note: Don't use this type for anything else other than exposing the
// original css property types in the Sail Docs website
export type CssPropertiesForSailDocs = Properties;
